import { createContext, useCallback, useMemo, useState, useContext, ReactNode } from "react";
import { startOfDay, subDays } from "date-fns";
import {
  PeriodType as ApiPeriodType,
} from "../../../common/data/service-proxies";

export type LocalPeriodType = "weekly" | "monthly";

export interface FuelEconomyReportContextValue {
  dateFrom: Date;
  dateTo: Date;
  periodType: LocalPeriodType;
  presetPeriod: unknown;
  isDownloading: boolean;
  apiPeriodType: ApiPeriodType | undefined;
  reportParams: {
    fromDate: Date;
    toDate: Date;
    periodType: ApiPeriodType | undefined;
  };
  setDateFrom: (d: Date) => void;
  setDateTo: (d: Date) => void;
  handlePeriodChange: (value: unknown) => void;
  setPresetPeriod: (v: unknown) => void;
  // handleDownload: () => Promise<void>;
}

export const FuelEconomyReportContext = createContext<FuelEconomyReportContextValue | null>(null);

export const useFuelEconomyReport = (): FuelEconomyReportContextValue => {
  const ctx = useContext(FuelEconomyReportContext);
  if (ctx == null) {
    throw new Error("useFuelEconomyReport must be used within FuelEconomyReportProvider");
  }
  return ctx;
};

interface FuelEconomyReportProviderProps {
  children: ReactNode;
}

export const FuelEconomyReportProvider = ({
  children,
}: FuelEconomyReportProviderProps) => {
  const [dateFrom, setDateFrom] = useState<Date>(() =>
    subDays(startOfDay(new Date()), 6)
  );
  const [dateTo, setDateTo] = useState<Date>(() => startOfDay(new Date()));
  const [periodType, setPeriodType] = useState<LocalPeriodType>("weekly");
  const [isDownloading] = useState(false);
  const [presetPeriod, setPresetPeriod] = useState<unknown>("");

  const apiPeriodType = useMemo<ApiPeriodType | undefined>(
    () => (periodType === "weekly" ? ApiPeriodType.Weekly : ApiPeriodType.Monthly),
    [periodType]
  );

  const reportParams = useMemo(
    () => ({
      fromDate: dateFrom,
      toDate: dateTo,
      periodType: apiPeriodType,
    }),
    [dateFrom, dateTo, apiPeriodType]
  );

  const handlePeriodChange = useCallback((value: unknown) => {
    const today = startOfDay(new Date());
    switch (value) {
      case "thisWeek":
        setPeriodType("weekly");
        setDateTo(today);
        setDateFrom(subDays(today, 6));
        break;
      case "lastWeek": {
        const end = subDays(today, 7);
        setPeriodType("weekly");
        setDateTo(end);
        setDateFrom(subDays(end, 6));
        break;
      }
      case "thisMonth":
        setPeriodType("monthly");
        setDateTo(today);
        setDateFrom(new Date(today.getFullYear(), today.getMonth(), 1));
        break;
      case "lastMonth": {
        const firstThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastMonthEnd = subDays(firstThisMonth, 1);
        setPeriodType("monthly");
        setDateTo(lastMonthEnd);
        setDateFrom(new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1));
        break;
      }
      default:
        break;
    }
  }, []);
  //TODO: adjust download logic when backend is ready
  // const handleDownload = useCallback(async () => {
  //   if (!axios) return;
  //   setIsDownloading(true);
  //   try {
  //     const api = ReportApiApiFactory(undefined, axios.defaults.baseURL, axios);
  //     const response = await api.reportSafetyreportPost({
  //       dateFrom: dateFrom.toISOString(),
  //       dateTo: dateTo.toISOString(),
  //       reportName: "fuel-economy",
  //     });
  //     await hub?.connection?.invoke("SubscribeToReportUpdates", response.data);
  //     history.push(`/${resourceNames.REPORTS}?tab=1`);
  //     notify("resources.reports.labels.reportRequested", "success");
  //   } catch (e) {
  //     notify("resources.reports.labels.error", "error");
  //     console.error(e);
  //   } finally {
  //     setIsDownloading(false);
  //   }
  // }, [axios, hub, notify, history, dateFrom, dateTo]);

  const value = useMemo<FuelEconomyReportContextValue>(
    () => ({
      dateFrom,
      dateTo,
      periodType,
      presetPeriod,
      isDownloading,
      apiPeriodType,
      reportParams,
      setDateFrom,
      setDateTo,
      handlePeriodChange,
      setPresetPeriod,
      // handleDownload,
    }),
    [
      dateFrom,
      dateTo,
      periodType,
      presetPeriod,
      isDownloading,
      apiPeriodType,
      reportParams,
      handlePeriodChange,
      // handleDownload,
    ]
  );

  return (
    <FuelEconomyReportContext.Provider value={value}>
      {children}
    </FuelEconomyReportContext.Provider>
  );
};
